import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAll } from '../../lib/save';
import { dayShort } from '../../lib/when';
import ListGrid from '../../components/ListGrid';
import PageBar from '../../components/PageBar';
import { Pill } from '../admin/adminUtils';
import { LocationName, RowButton, locationName } from './parts';
import PageLoading from '../../components/PageLoading';

/*
  History: every count this person has handed in, across every count there has
  been.

  Built to Group C's approved drawing on 5 September 2026.

  **There were two copies of it and they disagreed about how much history there
  is.** `DesktopHistory` asked for the most recent 50 and `MobileHistory` asked
  for the most recent 30, so the same rep saw a different number of their own
  counts depending on what they were holding, with nothing on either screen
  saying the list had been cut short. Both are gone; this reads the lot, a page
  at a time.

  **A rep could not open anything from here**, which is the other thing the
  drawing changes. It was a list of names with no way into any of them, and the
  count sheets it names are the only record of what was on a shelf last quarter.
*/
export default function History({ profile, navigate }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  /*
    **Two filters, from the drawing**: which count, and which state. A rep who
    has been here two years has hundreds of rows and one question, "what did I
    hand in last quarter", and there was no way to ask it.
  */
  const [cycleFilter, setCycleFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!profile?.id) { setLoading(false); return; }
    let live = true;
    /*
      **Every one of them, a page at a time.** A request brings back about a
      thousand rows and says nothing about the rest, and the two versions of this
      screen were each asking for a fixed handful on top of that. A history that
      quietly stops is worse than one that is slow.
    */
    fetchAll(() => supabase
      .from('inventory_counts')
      .select('id, status, submitted_at, approved_at, cycle:count_cycles(id, name), warehouse:warehouses(id, name, display_name, region_prefix)')
      .eq('rep_id', profile.id)
      .in('status', ['submitted', 'approved'])
      .order('id'))
      .then(({ data }) => {
        if (!live) return;
        setRows(data || []);
        setLoading(false);
      });
    return () => { live = false; };
  }, [profile]);

  if (loading) return <PageLoading rows={6} />;

  // The counts this person actually has history in, newest first, for the
  // picker. A count they never worked in is not an option worth offering.
  const cycles = [];
  const seen = new Set();
  for (const r of rows) {
    const c = r.cycle;
    if (c?.id && !seen.has(c.id)) { seen.add(c.id); cycles.push(c); }
  }

  const shown = rows
    .filter(r => !cycleFilter || r.cycle?.id === cycleFilter)
    .filter(r => statusFilter === 'all' || r.status === statusFilter)
    // **Alphabetical**, which is what the drawing shows and what makes a list
    // of a hundred locations findable. It came back newest first, which is the
    // order they happened in and not an order anybody looks in.
    .slice()
    .sort((a, b) => locationName(a).localeCompare(locationName(b)));

  const countLine = cycleFilter || statusFilter !== 'all'
    ? shown.length + ' of ' + rows.length
    : rows.length + ' across ' + cycles.length + ' count' + (cycles.length !== 1 ? 's' : '');

  const open = r => navigate('/count/' + r.id);

  return (
    <>
      {/* **The two pickers sit at the right end**, which is where the drawing
          puts them, so they are handed in as the bar's actions rather than as
          its controls: controls sit next to the page's name and actions sit
          opposite it. */}
      <PageBar
        title="History"
        actions={
          <>
            <select className={'tb-select' + (cycleFilter ? ' set' : '')}
              value={cycleFilter} onChange={e => setCycleFilter(e.target.value)}>
              <option value="">Every count</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className={'tb-select' + (statusFilter !== 'all' ? ' set' : '')}
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">Every status</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
            </select>
          </>
        }
      />

      <div className="region-block">
        {/* One quiet line naming the list and how long it is, which the drawing
            gives its own band above the columns. */}
        <div style={{ background: 'var(--surface-card)', padding: '13px 20px', borderBottom: '1px solid var(--border-soft)', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--text-primary)' }}>
            Every count you have handed in
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{countLine}</span>
        </div>
        <div className="region-detail">
          <ListGrid
            columns={[
              { label: 'Location', width: '4fr', midWidth: '2.6fr' },
              { label: 'Count', width: '1.4fr', midWidth: '1.3fr' },
              // The date is the column a narrower screen can spare: the state
              // pill beside it already says whether it is done with.
              { label: 'Submitted', width: '1fr', hideMid: true },
              { label: 'Status', width: '1fr', midWidth: '1.1fr' },
              { label: '', width: '1.2fr', midWidth: '1.4fr', align: 'right' },
            ]}
            rows={shown}
            keyOf={r => r.id}
            empty={rows.length === 0
              ? 'You have not handed in a count yet.'
              : 'No counts match the current filter.'}
            cells={r => [
              <LocationName p={r} />,
              <span style={{ color: 'var(--text-secondary)' }}>{r.cycle?.name || 'No count'}</span>,
              <span style={{ color: 'var(--text-secondary)' }}>{r.submitted_at ? dayShort(r.submitted_at) : 'Not submitted'}</span>,
              <Pill status={r.status} />,
              <RowButton tone="quiet" onClick={() => open(r)}>View count</RowButton>,
            ]}
            // The narrow shape the drawing asks for: the territory and the name,
            // then the state, which count it was and when, on one line.
            stack={r => (
              <>
                <LocationName p={r} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 7 }}>
                  <Pill status={r.status} />
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {(r.cycle?.name || 'No count') + (r.submitted_at ? ' - ' + dayShort(r.submitted_at) : '')}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <RowButton tone="quiet" onClick={() => open(r)}>View count</RowButton>
                </div>
              </>
            )}
          />
        </div>
      </div>

      {(cycleFilter || statusFilter !== 'all') && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn btn-outline" onClick={() => { setCycleFilter(''); setStatusFilter('all'); }}>
            Clear the filters, show all
          </button>
        </div>
      )}
    </>
  );
}
